import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { useStore } from 'src/stores/store';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import Label from 'src/components/Label';
import { IElectricityPayment } from 'src/@types/Choqorak/MainTables/ElectricityPayment/ElectricityPayment';
import useThrottle from 'src/hooks/useThrottle';
import { GetMonthName } from 'src/sections/common/GetMonthName';
// ----------------------------------------------------------------------
interface Props {
  id: number;
  year?: number;
  month?: number;
  searchBy?: string;
}
export default observer(function ELectricConsumption({ id, year, month, searchBy }: Props) {
  const { ElectricMeterStore, ElectricityPaymentStore } = useStore();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { setOpenCloseConsumptionDialog, selectedElectricMeter } = ElectricMeterStore;
  const { createElectricityPayment, updateElectricityPayment } = ElectricityPaymentStore;
  const { loadElectricConsumptionPayment } = ElectricMeterStore;
  const NewRentPaymentSchema = useMemo(
    () =>
      Yup.object().shape({
        currentElectricMeterDegree: Yup.number()
          .typeError(translate('Validation.currentElectricMeterDegree'))
          .required(translate('Validation.currentElectricMeterDegree'))
          .min(
            selectedElectricMeter?.lastElectricMeterDegree ?? 0,
            translate('Validation.minElectricDegree', {
              value: selectedElectricMeter?.lastElectricMeterDegree,
            })
          ),
      }),
    [selectedElectricMeter]
  );

  const defaultValues = useMemo<IElectricityPayment>(
    () => ({
      electricMeterId: selectedElectricMeter?.electricMeterId,
      year: Number(year) || undefined,
      month: Number(month) || undefined,
      currentElectricMeterDegree: undefined,
      searchBy: searchBy,
    }),
    [selectedElectricMeter, searchBy, year, month]
  );

  const methods = useForm<IElectricityPayment>({
    resolver: yupResolver(NewRentPaymentSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    watch,
    setError,
    control,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = useThrottle((data: IElectricityPayment) => {
    createElectricityPayment(data)
      .then(() => {
        loadElectricConsumptionPayment({
          year: Number(year),
          month: Number(month),
          searchBy: searchBy,
        });
        setOpenCloseConsumptionDialog();
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
      })
      .catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error.currentElectricMeterDegree != null) {
          setError('afterSubmit', { ...err, message: json.error.currentElectricMeterDegree });
        } else if (json.message != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      });
  });

  const val = watch();

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3} sx={{ p: 3 }}>
        {/* First column for the form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
              }}
            >
              {/* {ConsumptionEditMode??(
              <LocalizDatePicker
                name="years"
                label={translate('Report.FromDate')}
                control={control}
                size={'small'}
                views={['year', 'month']}
              />
            )} */}
              <RHFTextField
                name="currentElectricMeterDegree"
                label={translate('ElectricMeter.CurrentDegree')}
                type="number"
              />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                // startIcon={
                //   !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                // }
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                {translate('CRUD.Save')}
              </LoadingButton>
              <Button
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  setOpenCloseConsumptionDialog();
                }}
              >
                {translate('CRUD.Close')}
              </Button>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{}}>
            <Label color="success" sx={{ m: 3 }}>
              <Typography variant="body1">
                {GetMonthName(month)}/ {year}
              </Typography>
            </Label>

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>{translate('Customer.Customer')}</TableCell>
                  <TableCell>{selectedElectricMeter!.customerName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('Shop.ShopCode')}</TableCell>
                  <TableCell>{selectedElectricMeter!.shopCode}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.ElectricMeterSerial')}</TableCell>
                  <TableCell>{selectedElectricMeter!.electricMeterSerial}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.LastElectricMeterDegree')}</TableCell>
                  <TableCell>{selectedElectricMeter?.lastElectricMeterDegree}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.CurrentDegree')}</TableCell>
                  <TableCell>{val.currentElectricMeterDegree}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.ElectricityPrice')}</TableCell>
                  <TableCell>{selectedElectricMeter!.pricePerKilowat}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.Usage')}</TableCell>
                  <TableCell>
                    {selectedElectricMeter?.lastElectricMeterDegree! <=
                    val.currentElectricMeterDegree!
                      ? Number(val.currentElectricMeterDegree) -
                        Number(selectedElectricMeter?.lastElectricMeterDegree)
                      : 0}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>{translate('ElectricMeter.UsagePrice')}</TableCell>
                  <TableCell>
                    {selectedElectricMeter?.lastElectricMeterDegree! <=
                    val.currentElectricMeterDegree!
                      ? (Number(val.currentElectricMeterDegree) -
                          Number(selectedElectricMeter?.lastElectricMeterDegree)) *
                        selectedElectricMeter?.pricePerKilowat!
                      : 0}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>

        {/* Second column for displaying year and month information */}
      </Grid>
    </FormProvider>
  );
});
